document.addEventListener('DOMContentLoaded', () => {
  const SECTION_ID = 'search-grid';
  const searchInput = document.getElementById('search-input');
  const suggestionList = document.getElementById('predictive-search-results-queries-list');
  const searchRow = document.querySelector('.search-row');
  const searchPageResults = document.querySelector('.search-results');
  const searchTitle = document.querySelector('.search-title');
  const showAllButton = document.querySelector('.show-all-result');
  let swiperInstance = null;

  // Fetch product cards via Section Rendering API
  const fetchCards = (handles) =>
    Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}?sections=${SECTION_ID}`)
          .then((r) => r.json())
          .then((json) => ({ handle, html: json[SECTION_ID] ?? '' }))
          .catch(() => ({ handle, html: '' }))
      )
    );

  // Swiper Init
  const initSwiper = () => {
    swiperInstance?.destroy?.(true, true);
    swiperInstance = null;

    const el = document.querySelector('.search-drawer-slider.swiper');
    if (!el) return;

    setTimeout(() => {
      swiperInstance = new Swiper(el, {
        slidesPerView: 'auto',
        loop: false,
        freeMode: { enabled: true, momentum: true, momentumBounce: true },
        grabCursor: true,
        speed: 800,
        mousewheel: { forceToAxis: true, sensitivity: 1, releaseOnEdges: true },
        keyboard: { enabled: true },
      });
    }, 50);
  };

  // ========================
  // TAGALYS SEARCH SUGGESTIONS (Drawer)
  // ========================
  const initTagalysSearch = () => {
    if (!window.Tagalys?.APIConnector?.SearchSuggestions) return;

    window.searchSuggestions = Tagalys.APIConnector.SearchSuggestions.new({
      params: {
        sections: {
          products: { include: ['total_count', 'items'], count: 10 },
        },
      },
      onSuccess(response) {
        const data = response.sections ?? response;
        renderDrawerSuggestions(data);
        renderDrawerProducts(data);
      },
      onFailure() {
        if (suggestionList) suggestionList.innerHTML = '<li class="predictive-search__list-item">Search unavailable</li>';
      },
    });

    if (searchInput) {
      // Drawer input handler
      searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim();
        if (!query) {
          if (suggestionList) suggestionList.innerHTML = '';
          if (searchRow) searchRow.innerHTML = '';
          return;
        }
        if (showAllButton) showAllButton.href = `/pages/search-results-page?query=${encodeURIComponent(query)}`;
        window.searchSuggestions.helpers.updateQuery(query);
      });

      // Redirect on Enter
      searchInput.addEventListener('keypress', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const query = searchInput.value.trim();
        if (query) {
          window.location.href = `/pages/search-results-page?query=${encodeURIComponent(query)}`;
        }
      });
    }
  };

  // Build a suggestion <li>
  const buildSuggestionLi = (text, href) => {
    const li = document.createElement('li');
    li.className = 'predictive-search__list-item';
    li.innerHTML =
      `<a href="${href}" class="hover-opacity predictive-search__item link link--text">` +
        '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
          `<p class="predictive-search__item-heading line-18">${text}</p>` +
        '</div>' +
      '</a>';
    return li;
  };

  // Render suggestion links (left drawer)
  const renderDrawerSuggestions = (data) => {
    if (!suggestionList) return;
    suggestionList.innerHTML = '<li class="sugg-title line-18">Suggested:</li>';

    const searches = data.queries?.items ?? [];
    const products = data.products?.items ?? [];

    if (searches.length === 0 && products.length === 0) {
      suggestionList.innerHTML += '<li class="predictive-search__list-item">No suggestions found</li>';
      return;
    }

    // Product suggestions as text links
    for (const p of products.slice(0, 8)) {
      const title = (p.name ?? p.title ?? '').split(' - ')[0].trim();
      const href = p.url ?? `/products/${p.handle}`;
      suggestionList.appendChild(buildSuggestionLi(title, href));
    }
  };

  // Render product cards in right drawer via Section Rendering API
  const renderDrawerProducts = (data) => {
    if (!searchRow) return;
    const products = data.products?.items ?? [];

    if (products.length === 0) {
      searchRow.innerHTML = '';
      return;
    }

    const handles = products.slice(0, 10).map((p) => p.handle).filter(Boolean);
    searchRow.innerHTML = '';

    fetchCards(handles).then((sections) => {
      for (const s of sections) {
        if (!s.html) continue;
        const slide = document.createElement('div');
        slide.classList.add('search-column', 'product-item', 'swiper-slide', 'border-right');
        slide.innerHTML = s.html;
        searchRow.appendChild(slide);
      }
      initSwiper();
      window.GroupSwatches?.init(searchRow);
    });
  };

  // ========================
  // TAGALYS SEARCH RESULTS PAGE
  // ========================
  const initTagalysSearchResults = () => {
    const pageQuery = new URLSearchParams(window.location.search).get('query');
    if (!pageQuery || !searchPageResults) return;

    const searchLoader = document.querySelector('.search-loader');
    const loadActions = document.querySelector('.search-load-actions');
    const loadMoreBtn = document.querySelector('.search-load-more');
    let isFirstLoad = true;

    const showLoader = () => { if (searchLoader) searchLoader.style.display = 'flex'; };
    const hideLoader = () => { if (searchLoader) searchLoader.style.display = 'none'; };

    if (!window.Tagalys?.APIConnector?.SearchResults) {
      if (searchPageResults) searchPageResults.innerHTML = '<div class="no-results">Search is currently unavailable.</div>';
      return;
    }

    window.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
      params: { query: pageQuery, perPage: 48 },
      onSuccess(response, helpers) {
        if (helpers.isRedirected()) {
          window.location.href = helpers.getRedirectURL();
          return;
        }

        const products = helpers.getProducts() ?? [];
        const totalCount = helpers.getTotalProductsCount() ?? 0;
        const query = helpers.getQuery() ?? pageQuery;

        if (isFirstLoad) {
          searchPageResults.innerHTML = '';
          isFirstLoad = false;

          if (searchTitle) {
            if (helpers.isSpellingCorrectedQuery()) {
              searchTitle.innerHTML = `No results for "${helpers.getOriginalQuery()}", showing results for "${query}"`;
            } else if (helpers.isPartialResults()) {
              searchTitle.innerHTML = `Showing partial results for "${query}"`;
            } else {
              searchTitle.innerHTML = `Showing ${totalCount} results for "${query}"`;
            }
          }
        }

        if (products.length === 0 && helpers.getCurrentPage() === 1) {
          searchPageResults.innerHTML = '<div class="no-results">No products found.</div>';
          hideLoader();
          return;
        }

        const handles = products.map((p) => p.handle).filter(Boolean);

        fetchCards(handles).then((sections) => {
          for (const s of sections) {
            if (!s.html) continue;
            const item = document.createElement('div');
            item.classList.add('search-column', 'product-item');
            item.innerHTML = s.html;
            searchPageResults.appendChild(item);
          }

          window.GroupSwatches?.init(searchPageResults);

          if (helpers.hasNextPage()) {
            const rendered = searchPageResults.querySelectorAll('.search-column.product-item').length;
            const remaining = totalCount - rendered;
            if (loadMoreBtn) loadMoreBtn.textContent = `View ${Math.min(remaining, 48)} More Items`;
            if (loadActions) loadActions.style.display = 'flex';
          } else {
            if (loadActions) loadActions.style.display = 'none';
          }
        }).finally(hideLoader);
      },
      onFailure() {
        searchPageResults.innerHTML = '<div class="error">Error loading search results.</div>';
        hideLoader();
      },
    });

    loadMoreBtn?.addEventListener('click', () => {
      if (!window.searchResultsInstance) return;
      if (loadActions) loadActions.style.display = 'none';
      showLoader();
      window.searchResultsInstance.helpers.goToNextPage();
    });

    showLoader();
    window.searchResultsInstance.helpers.call();
  };

  // ========================
  // INIT
  // ========================
  onTagalysReady(() => {
    initTagalysSearch();
    initTagalysSearchResults();
  });
});
